import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { config } from './config';

/**
 * The telemetry: loading (failures reported meanwhile wait for it, a few at most), loaded, or
 * absent (no connection string, or the script was blocked or could not be fetched).
 */
let insights: ApplicationInsights | 'loading' | null = null;
const waiting: { exception: Error; where: string }[] = [];
const MAX_WAITING = 20;

/**
 * Application Insights, loaded lazily and only when a connection string is configured.
 * No cookies, no user identifiers: errors and performance only. Nothing is sampled away: the
 * workspace's daily cap bounds the cost, and a crash seen on one headset may be reported only once
 * (client-audio-engine:quality#9).
 */
export function initTelemetry(): void {
  if (!config.appInsightsConnectionString || insights) return;
  insights = 'loading';
  import('@microsoft/applicationinsights-web')
    .then(({ ApplicationInsights }) => {
      const loaded = new ApplicationInsights({
        config: {
          connectionString: config.appInsightsConnectionString,
          disableCookiesUsage: true,
          enableAutoRouteTracking: false,
          samplingPercentage: 100,
          enableUnhandledPromiseRejectionTracking: true,
        },
      });
      loaded.loadAppInsights();
      loaded.context.application.ver = `${config.version}+${config.commit}`;
      loaded.trackPageView();
      insights = loaded;
      for (const { exception, where } of waiting.splice(0)) send(loaded, exception, where);
    })
    .catch(() => {
      // An ad-blocker or an offline headset: play on without telemetry.
      insights = null;
      waiting.length = 0;
    });
}

/**
 * A failure the player was shown (the table could not be built, the page stopped), reported
 * with where it happened rather than left to the unhandled-rejection hook
 * (client-audio-engine:quality#1).
 */
export function reportError(error: unknown, where: string): void {
  const exception = error instanceof Error ? error : new Error(String(error));
  console.error(`${where} failed`, error);
  if (insights === 'loading') {
    if (waiting.length < MAX_WAITING) waiting.push({ exception, where });
  } else if (insights) send(insights, exception, where);
}

function send(to: ApplicationInsights, exception: Error, where: string): void {
  to.trackException({ exception, properties: { where } });
}
